import React, { useState } from 'react';
import { Archive, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../AppProvider';

interface DealStatusControlProps {
  dealId: string;
  isActive: boolean;
  onStatusChange?: (isActive: boolean) => void;
}

export function DealStatusControl({ dealId, isActive, onStatusChange }: DealStatusControlProps) {
  const { toast, confirm } = useApp();
  const [updating, setUpdating] = useState(false);
  
  const toggleDealStatus = async () => {
    // Se stiamo disattivando un contratto, chiediamo conferma
    if (isActive) {
      const confirmed = await confirm({
        title: 'Termina Contratto',
        message: 'Sei sicuro di voler terminare questo contratto? Il contratto verrà segnato come inattivo e non genererà più provvigioni mensili. Questa azione può essere annullata riattivando il contratto successivamente.',
        confirmText: 'Termina Contratto',
        cancelText: 'Annulla',
        confirmVariant: 'warning'
      });
      
      if (!confirmed) return;
    }
    
    setUpdating(true);
    
    try {
      const { error } = await supabase
        .from('deals')
        .update({
          is_active: !isActive,
          deactivated_at: isActive ? new Date().toISOString() : null, // Set when deactivating, clear when reactivating
        })
        .eq('id', dealId);
        
      if (error) throw error;
      
      toast.success(isActive 
        ? 'Contratto terminato con successo' 
        : 'Contratto riattivato con successo');
        
      if (onStatusChange) {
        onStatusChange(!isActive);
      }
    } catch (error) {
      console.error('Error updating deal status:', error);
      toast.error('Errore durante l\'aggiornamento dello stato del contratto');
    } finally {
      setUpdating(false);
    }
  };
  
  return (
    <div className="glass-card rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {isActive ? (
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
          ) : (
            <Archive className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
          )}
          <span className="text-sm font-medium text-black dark:text-white">
            Stato contratto: {isActive ? 'Attivo' : 'Terminato'}
          </span>
        </div>
        <button
          onClick={toggleDealStatus}
          disabled={updating}
          className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${
            isActive
              ? 'glass-card border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
              : 'glass-card border border-green-300 dark:border-green-600 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
          }`}
        >
          {updating ? (
            <span className="animate-pulse">Aggiornamento...</span>
          ) : isActive ? (
            <>
              <Archive className="h-4 w-4 mr-1" />
              Termina contratto
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-1" />
              Riattiva contratto
            </>
          )}
        </button>
      </div>
      {isActive && (
        <p className="mt-2 text-sm text-black/60 dark:text-white/70">
          Il contratto è attivo e genera provvigioni mensili per i servizi con fatturazione ricorrente.
        </p>
      )}
      {!isActive && (
        <div className="mt-2 flex items-start">
          <AlertCircle className="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 mr-1.5 flex-shrink-0" />
          <p className="text-sm text-black/60 dark:text-white/70">
            Il contratto è terminato e non genera più provvigioni mensili. Puoi riattivarlo in qualsiasi momento.
          </p>
        </div>
      )}
    </div>
  );
}